import { inject, Injectable, signal } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_BASE_HREF } from '@angular/common';
import {
  AUTH_API_URL,
  AUTH_REDIRECT_URL,
  AUTH_STORAGE,
  AUTH_TOKEN_STORAGE_KEY,
} from './auth.tokens';
import { finalize, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { AuthToken } from './models/auth-data';
import { AuthEventType } from './models/auth-event-type.enum';
import { Router } from '@angular/router';
import { ResetEmailPayload } from './models/reset-email-payload';
import { ResetPayload } from './models/reset-payload';
import { environment } from '../../../environments/environment';
import { LanguageService } from '../i18n/language.service';
import {
  AuthEmailCodeVerificationPayload,
  AuthEmailLoginPayload,
  AuthPhoneOtpPayload,
  AuthPhoneOtpVerificationPayload,
  AuthRegisterV2Payload,
} from './models/auth-v2-payloads';

const destinationToken = 'AUTH_REDIRECT_DESTINATION';
const CURRENT_USER_CACHE_KEY = 'CURRENT_USER_CACHE_V1';
const CURRENT_USER_CACHE_TTL_MS = 5 * 60 * 1000;
const NEW_TAB_TOKEN_TRANSFER_KEY = 'AUTH_NEW_TAB_TOKEN_TRANSFER_V1';
const NEW_TAB_TOKEN_TRANSFER_TTL_MS = 10 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  // Refresh bypasses interceptors so its 401 cannot start another refresh.
  private refreshHttp = new HttpClient(inject(HttpBackend));
  private refreshRequest$?: Observable<AuthToken>;
  private router = inject(Router);
  private authStorage = inject(AUTH_STORAGE);
  private authTokenLocalstorageKey: string = inject(AUTH_TOKEN_STORAGE_KEY);
  private authRedirectUrl: string = inject(AUTH_REDIRECT_URL);
  private authApiUrl: string = inject(AUTH_API_URL);
  private baseHref: string = inject(APP_BASE_HREF);
  private redirectUrl: string = inject(AUTH_REDIRECT_URL);
  private languageService = inject(LanguageService);

  baseUrl = environment.baseUrl;
  private readonly authV2Url = `${this.baseUrl}/corporate-client/v2`;

  private events$ = new Subject<AuthEventType>();

  private currentUserSubject = new BehaviorSubject<any>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private passwordRequired = signal(this.getToken()?.need_password === true);
  readonly needPassword = this.passwordRequired.asReadonly();

  get authEvents$() {
    return this.events$.asObservable();
  }

  refreshToken(token: AuthToken): Observable<AuthToken> {
    if (this.refreshRequest$) return this.refreshRequest$;

    const url = `${this.baseUrl}/corporate-client/refresh`;
    this.refreshRequest$ = this.refreshHttp
      .post<AuthToken>(url, { refresh_token: token.refresh_token })
      .pipe(
        tap((response) => {
          if (
            typeof response?.token !== 'string' ||
            typeof response?.refresh_token !== 'string' ||
            !response.token ||
            !response.refresh_token
          ) {
            throw new HttpErrorResponse({ status: 502, url });
          }
          this.storeToken({ ...this.getToken(), ...response });
        }),
        takeUntil(this.events$),
        finalize(() => {
          this.refreshRequest$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshRequest$;
  }

  redirectToAuth(): void {
    this.setDestination();
    window.location.replace(
      `${this.authRedirectUrl}&redirect_uri=${this.getRedirectBackUrl()}`
    );
  }

  setUser(user: any) {
    this.currentUserSubject.next(user);
  }

  private readCachedUser(): any | null {
    try {
      const raw = localStorage.getItem(CURRENT_USER_CACHE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const isExpired =
        Date.now() - parsed.cachedAt > CURRENT_USER_CACHE_TTL_MS;
      if (isExpired) return null;

      return parsed.user ?? null;
    } catch {
      return null;
    }
  }

  private writeCachedUser(user: any) {
    localStorage.setItem(
      CURRENT_USER_CACHE_KEY,
      JSON.stringify({ user, cachedAt: Date.now() })
    );
  }

  private clearCachedUser() {
    localStorage.removeItem(CURRENT_USER_CACHE_KEY);
  }

  login(data: any): Observable<AuthToken> {
    return this.http.post<AuthToken>(this.authApiUrl, data).pipe(
      tap((res: AuthToken) => {
        this.setToken(res);
      })
    );
  }

  loginByEmail(data: AuthEmailLoginPayload): Observable<AuthToken> {
    return this.http
      .post<AuthToken>(`${this.authV2Url}/login/email`, data)
      .pipe(
        tap((res: AuthToken) => {
          this.setToken(res);
        })
      );
  }

  sendPhoneLoginOtp(data: AuthPhoneOtpPayload): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/login/phone/send-otp`, data);
  }

  verifyPhoneLoginOtp(
    data: AuthPhoneOtpVerificationPayload
  ): Observable<AuthToken> {
    return this.http
      .post<AuthToken>(`${this.authV2Url}/login/phone/verify-otp`, data)
      .pipe(
        tap((res: AuthToken) => {
          this.setToken(res);
        })
      );
  }

  getCurrentUser(force = false) {
    if (!force) {
      const cached = this.readCachedUser();
      if (cached) {
        this.currentUserSubject.next(cached);

        const language = cached?.language || cached?.data?.language;
        if (language) {
          this.languageService.toggleLanguageByCode(language, false);
        }

        return new Observable<any>((subscriber) => {
          subscriber.next(cached);
          subscriber.complete();
        });
      }
    }

    return this.http.get(`${this.baseUrl}/corporate-client/me`).pipe(
      tap((user: any) => {
        this.currentUserSubject.next(user);
        this.writeCachedUser(user);

        const language = user?.language || user?.data?.language;
        if (language) {
          this.languageService.toggleLanguageByCode(language, false);
        }
      })
    );
  }

  register(data: any) {
    return this.http.post(`${this.baseUrl}/corporate-client/register`, data);
  }

  registerByEmail(data: AuthRegisterV2Payload): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/register/email`, data);
  }

  verifyEmailRegistrationCode(
    data: AuthEmailCodeVerificationPayload
  ): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/register/email/verify`, data);
  }

  registerByPhone(data: AuthRegisterV2Payload): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/register/phone`, data);
  }

  verifyPhoneRegistrationCode(
    data: AuthPhoneOtpVerificationPayload
  ): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/register/phone/verify`, data);
  }

  resendOtp(data: AuthPhoneOtpPayload): Observable<unknown> {
    return this.http.post(`${this.authV2Url}/resend-otp`, data);
  }

  searchByBin(bin: string) {
    return this.http.post(`${this.baseUrl}/egov/dadata/search-by-bin`, {
      bin: bin,
    });
  }

  verifyCode(email: any, code: any) {
    return this.http.post(`${this.baseUrl}/corporate-client/auth/verify-code`, {
      email,
      code,
    });
  }

  logout(): void {
    this.removeToken();
    this.clearCachedUser();
    this.setUser(null);
    this.router.navigateByUrl('/' + this.redirectUrl);
  }

  removeToken(): void {
    this.authStorage.removeItem(this.authTokenLocalstorageKey);
    localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
    this.passwordRequired.set(false);
    this.events$.next(AuthEventType.LOGOUT);
  }

  getToken(): AuthToken | null {
    const token = this.authStorage.getItem(this.authTokenLocalstorageKey);
    if (token) return JSON.parse(token);

    const transferredToken = this.readTransferredToken();
    if (!transferredToken) return null;

    this.authStorage.setItem(
      this.authTokenLocalstorageKey,
      JSON.stringify(transferredToken)
    );

    return transferredToken;
  }

  private getRedirectBackUrl(): string {
    return `${window.location.origin + this.baseHref}`;
  }

  private setDestination() {
    localStorage.setItem(destinationToken, window.location.href);
  }

  setToken(token: AuthToken) {
    this.storeToken(token);
    this.events$.next(AuthEventType.LOGIN);
  }

  private storeToken(token: AuthToken): void {
    this.authStorage.setItem(
      this.authTokenLocalstorageKey,
      JSON.stringify(token)
    );
    localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
    this.passwordRequired.set(token.need_password === true);
  }

  markPasswordSet(): void {
    const token = this.getToken();
    if (!token) return;

    this.authStorage.setItem(
      this.authTokenLocalstorageKey,
      JSON.stringify({ ...token, need_password: false })
    );
    localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
    this.clearCachedUser();
    this.passwordRequired.set(false);
  }

  prepareTokenTransferForNewTab(): void {
    const token = this.getToken();
    if (!token) return;

    const payload = {
      token,
      expiresAt: Date.now() + NEW_TAB_TOKEN_TRANSFER_TTL_MS,
    };

    localStorage.setItem(NEW_TAB_TOKEN_TRANSFER_KEY, JSON.stringify(payload));

    window.setTimeout(() => {
      const raw = localStorage.getItem(NEW_TAB_TOKEN_TRANSFER_KEY);
      if (raw === JSON.stringify(payload)) {
        localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
      }
    }, NEW_TAB_TOKEN_TRANSFER_TTL_MS);
  }

  private readTransferredToken(): AuthToken | null {
    try {
      const raw = localStorage.getItem(NEW_TAB_TOKEN_TRANSFER_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
        localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
        return null;
      }

      return parsed.token ?? null;
    } catch {
      localStorage.removeItem(NEW_TAB_TOKEN_TRANSFER_KEY);
      return null;
    }
  }

  sendEmailToResetPassword(
    resetEmailData: ResetEmailPayload
  ): Observable<unknown> {
    return this.http.post<unknown>(
      '/corporate-client/password/forgot',
      resetEmailData
    );
  }

  resetPassword(resetPayload: ResetPayload): Observable<unknown> {
    return this.http.post<unknown>(
      '/corporate-client/auth/reset',
      resetPayload
    );
  }
}
